import * as React from 'react';
import cx from 'classnames';
import {
  every,
  filter,
  find,
  findIndex,
  forEach,
  includes,
  isArray,
  isEmpty,
  isEqual,
  isFunction,
  isNil,
  isNumber,
  isUndefined,
  map,
  some,
} from 'lodash';

import { SubmitButton } from 'src/widgets/Button';
import {
  IToastRefHandles,
  Toast,
} from 'src/widgets/Toast';
import { ICommunity } from 'src/common/models/community';
import { ISocialAccount } from 'src/common/models/socialAccount';
import { IProspect } from 'src/common/models/member';
import { IMemberProgram, IMemberProgramMap, TProgram } from 'src/common/models/program';
import { InviteIcon } from 'src/icons';
import { IOption, ISelectProps, Select } from 'src/widgets/Select/Select';
import { Loading } from 'src/widgets/Skeleton/Loading';
import { Tooltip } from 'src/widgets/Tooltip';


import { useInvite, useInviteContext } from './hooks';
import {
  getProgramStatusOfAccount,
  getCommunitiesAndProgramsOfProspect,
  getProgramStatus,
  getRecentlyCreatedProgramId,
  getSocialAccountsFromProspects,
  mapProgramsToCommunities,
} from './utils';

const { useEffect, useRef, useState, useMemo } = React;
import styles from './Invite.scss';

type TInviteMode = 'default' | 'withButton';

enum Reason {
  IsFetching = 'IsFetching',
  NoProspect = 'NoProspect',
  NoPrograms = 'NoPrograms',
  NoResource = 'NoResource',
  NoEmail = 'NoEmail',
  HasStatus = 'HasStatus',
  Inviting = 'Inviting',
  InvitingThisProspect = 'InvitingThisProspect', // Used for main button
  InvitingThisProspectToSelectedProgram = 'InvitingThisProspectToSelectedProgram', // Used for button in list
}

interface IReasonDescription {
  message: string;
  ammendFilter?: (prospects: IProspect[], programId?: TProgram['id'], memberPrograms?: IMemberProgramMap) => IProspect[];
  showOnlyOnListMode?: boolean;
  hideFromDropdown?: boolean;
}

export const ReasonsDescriptions: Record<Reason, IReasonDescription> = {
  [Reason.IsFetching]: {
    message: 'Project info is loading. This may take a few moments.',
  },
  [Reason.NoProspect]: {
    message: 'Please select at least one creator to invite.',
  },
  [Reason.NoPrograms]: {
    message: `You haven't created any project yet to invite. Please go to Projects and create one.`,
  },
  [Reason.NoResource]: {
    message: 'Your email is not connected. Please confirm your Gmail or Outlook is connected or try to reconnect.',
  },
  [Reason.NoEmail]: {
    message: 'Creator cannot be invited because the email address is not provided.',
    ammendFilter: (prospects) => filter(
      prospects, 
      (prospect) => {
        const socialAccount: ISocialAccount = prospect;
        return (socialAccount.has_email && socialAccount.can_contact);
      }
    ),
    showOnlyOnListMode: true,
  },
  [Reason.HasStatus]: {
    message: 'Creator has been previously invited to this project.',
    hideFromDropdown: true,
  }, 
  [Reason.Inviting]: {
    message: 'Inviting selected creators. This may take a few moments.',
  },
  [Reason.InvitingThisProspect]: {
    message: 'Inviting selected creators. This may take a few moments.',
  },
  [Reason.InvitingThisProspectToSelectedProgram]: {
    message: 'Inviting selected creators. This may take a few moments.',
  },
};

export interface IInviteProps {
  className?: string;
  /**
   * Prospect
   */
  prospect: IProspect | IProspect[];
  /**
   * Pre-load all programs of these prospects
   */
  preloadProspects?: IProspect | IProspect[];
  /**
   * Invite mode
   */
  mode?: TInviteMode;
  /**
   * Show border
   */
  withBorder?: boolean;
  /**
   * Props forwarded to <Select>
   */
  selectProps?: Omit<Partial<ISelectProps>, 'options'>;
  /**
   * Ref to Toast
   * if not provided, Invite will have its own Toast
   */
  toastRef?: React.RefObject<IToastRefHandles>;
  /**
   * Callback when popover is shown/hidden
   */
  onPopoverShown?: (shown: boolean) => void;
  /**
   * Callback when a prospect is invited
   */
  onInvite?: (prospect: IProspect | IProspect[], programs: TProgram[]) => void;
  /**
   * Callback when a program is selected
   */
  onProgramSelect?: (programId: TProgram['id']) => void;
}

export interface IInviteStatus {
  className?: string;
  message: React.ReactChild;
  shortMessage?: string;
}

/**
 * Invite a member to a program
 */
export const Invite: React.FC<IInviteProps> = (props: IInviteProps) => {
  const {
    className,
    mode = 'default',
    onInvite: onInviteProp,
    onPopoverShown = () => undefined,
    onProgramSelect = () => undefined,
    preloadProspects = props.prospect,
    prospect: prospects,
    selectProps = {},
    toastRef: toastRefProp,
    withBorder = false,
  } = props;

  const isListView = mode !== 'default';

  // Force rerender on invite
  const onInvite = (prospect: IProspect | IProspect[], programs: TProgram[]) => {
    if (isFunction(onInviteProp)) {
      onInviteProp(prospect, programs);
    }
    forceUpdate((x) => !x);
  };

  const {
    memberPrograms,
    selectedProgramId,
    updateSelectedProgramId,
    isWorkflowEnabled,
    updatedSelectedSocialAccounts,
  } = useInviteContext();

  const {
    communities,
    sendInvite,
    error,
    inviteStatus,
    isFetching,
    programs,
    resourceId,
    allPrograms,
  } = useInvite({
    prospect: prospects,
    preloadProspects,
    onInvite,
  });

  const [, forceUpdate] = useState(false);
  const [isPopoverShown, setIsPopoverShown] = useState(false);
  const selectRef = useRef<Select>(); // Used to control the selected option
  const mainContainerRef = useRef<HTMLDivElement>(); // Used to make popover mount on this component's root container
  const toastRef = useRef<IToastRefHandles>(toastRefProp ? toastRefProp.current : null);
  const submitButtonRef = useRef<HTMLDivElement>();

  // Get social accounts of provided prospect/s
  const prospectAccounts = isArray(prospects)
    ? getSocialAccountsFromProspects(...prospects)
    : getSocialAccountsFromProspects(prospects);

  // Only used for 'default' (single account) mode
  const programStatusOfProspect = prospectAccounts.length > 0
    ? getProgramStatusOfAccount(
      prospectAccounts[0],
      selectedProgramId,
      memberPrograms,
    )
    : null;

  const isProspectContactable = (account: ISocialAccount): boolean => (
    account.has_email && account.can_contact
  );

  const areProspectsContactable = (prospectAccounts: Partial<ISocialAccount[]>): boolean => (
    every(prospectAccounts, (account) => isProspectContactable(account))
  );

  // Used to disable invite button when inviting
  const blockedReasons: Reason[] = (() => {
    const r: Reason[] = [];
    if (isFetching) {
      r.push(Reason.IsFetching);
    }
    if (isEmpty(prospects)) {
      r.push(Reason.NoProspect);
    }
    if (!isNumber(resourceId)) {
      r.push(Reason.NoResource);
    }
    if (!areProspectsContactable(prospectAccounts)) {
      r.push(Reason.NoEmail);
    }
    if (!isNil(programStatusOfProspect)) {
      r.push(Reason.HasStatus);
    }
    if (inviteStatus) {
      if (inviteStatus.isInviting) {
        r.push(Reason.Inviting);
        if (isEqual(inviteStatus.prospect, prospects)) {
          r.push(Reason.InvitingThisProspect);
          if (selectedProgramId === inviteStatus.programId) {
            r.push(Reason.InvitingThisProspectToSelectedProgram);
          }
        }
      }
    }
    return r;
  })();

  const isInviteBlocked = blockedReasons.length > 0; // If you only care about blocking for any reason at all

  /**
   * Render button element
   */
  const isSelectingDisabled = some(blockedReasons, (reason) => {
    switch (reason) {
      case Reason.IsFetching:
      case Reason.NoResource:
      case Reason.NoEmail:
        return true;
      default:
        return false;
    }
  });

  // Update whichever's listening to popover opened/closed status
  useEffect(() => {
    onPopoverShown(isPopoverShown);
  }, [isPopoverShown, onPopoverShown]);

  /**
   * On 'withButton' or mass invite mode, show default list
   * On solo prospect mode, show its own programs with respective statuses
   */
  const communitiesAndPrograms: readonly ICommunity[] = (
    mode === 'withButton' || isArray(prospects) || isEmpty(prospects)
      ? mapProgramsToCommunities(communities, programs)
      : getCommunitiesAndProgramsOfProspect(communities, memberPrograms, programs, prospects)
  );

  /**
   * Get a TProgram[] from all accounts
   */
  const allProgramsForProspect: TProgram[] = useMemo(() => {

    if (isEmpty(prospectAccounts)) {
      return allPrograms;
    }

    const { id: socialAccountId } = prospectAccounts[0];

    if (!socialAccountId) {
      return allPrograms;
    }

    const prospectPrograms = memberPrograms[socialAccountId];

    return map(allPrograms, (program): TProgram => {
      const { id } = program;

      const prospectProgram: IMemberProgram = find(
        prospectPrograms,
        (prospectProgram) => prospectProgram.id === id
      );

      return prospectProgram || program;
    });
  }, [prospectAccounts, allPrograms, memberPrograms]);

  /**
   * Show error message
   */
  useEffect(() => {
    if (error) {
      toastRef.current.showMessage({
        type: 'error',
        content: error.toString(),
      });
    }
  }, [error]);

  /**
   * Status message shown at the top of the list
   */

  const filterProspects = (filterFn: IReasonDescription['ammendFilter']): void => {
    const newProspects = filterFn(
      isArray(prospects) ? prospects : [prospects], 
      selectedProgramId, 
      memberPrograms
    );

    const socialAccounts: ISocialAccount[] = map(newProspects, 'socialAccount');
    updatedSelectedSocialAccounts(socialAccounts);
  };

  const getExplainedBlockedReason = (reasonDescription: IReasonDescription): IInviteStatus => {
    const {
      message,
      showOnlyOnListMode = false,
      ammendFilter,
    } = reasonDescription;

    if (
      (showOnlyOnListMode && !isListView)
        || reasonDescription.hideFromDropdown
    ) {
      return;
    }

    let formattedMessage: React.ReactElement = <>{message}</>;

    if (
      isListView
        && isFunction(ammendFilter)
    ) {
      formattedMessage = <>
        {formattedMessage}
        <strong
          className={styles.ammendAction}
          onClick={() => filterProspects(ammendFilter)}
        >
          Remove uninvitable creators.
        </strong>
      </>;
    }

    return {
      message: formattedMessage,
      className: styles.formattedBlockedReason,
    };
  };


  const statusMessage = ((): IInviteStatus => {
    /** We only show one status message. Grab the first one that has description. */
    const firstBlockedReason = find(blockedReasons, (reason) => !isUndefined(ReasonsDescriptions[reason]));

    if (firstBlockedReason) {
      const explainedReason = getExplainedBlockedReason(ReasonsDescriptions[firstBlockedReason]);
      if (explainedReason) {
        return explainedReason;
      }
    }

    if (!isNumber(resourceId)) {
      return {
        message: 'Your email is not connected. Please confirm your Gmail or Outlook is connected or try to reconnect.',
      };
    } else if (isEmpty(communitiesAndPrograms)) {
      return {
        message: 'There are no groups yet',
        className: styles.noCommunities,
      };
    }

    if (
      !isArray(prospects)
        && mode === 'default'
        && includes(blockedReasons, Reason.NoEmail)
    ) {
      return {
        message: <>
          Creators cannot be invited because their email address is not provided.
          Instead, you may directly reach out to them &nbsp;
          <a
            className={styles.accountLink}
            href={prospectAccounts[0].link}
            target='_blank'
            rel='noopener noreferrer'
            onClick={() => {
              window.open(prospectAccounts[0].link, '_blank');
            }}
          >
            {prospectAccounts[0].username ? `at @${prospectAccounts[0].username}` : 'here'}
          </a>.
        </>,
        className: styles.ineligible,
      };
    }
  })();

  /**
   * Generate the list option for a program
   */
  const getProgramOption = (program: TProgram): IOption => {
    const status = getProgramStatus(program);
    const isInvitingToThisProgram = inviteStatus.isInviting && program.id === inviteStatus.programId;

    const actions: React.ReactChild[] = [];
    if (mode === 'default') {
      if (status === 'invited' || status === 'new' || status === 'approved') {
        // Member status ('rejected' not being shown)
        actions.push(
          <div key={status} className={styles[status]}>
            {(() => {
              if (status === 'approved') {
                return 'Member';
              } else if (status === 'new') {
                return 'Applied';
              }
              return status;
            })()}
          </div>
        );
      } else if (!status && !isInviteBlocked) {
        // Invite button
        actions.push(
          <SubmitButton
            className={cx(styles.inviteButton, {
              [styles.inviting]: isInvitingToThisProgram,
              [styles.wait]: includes(blockedReasons, Reason.Inviting),
            })}
            key='inviteButton'
            isSubmitting={isInvitingToThisProgram}
            label='Invite'
            submittingLabel=''
            icon={<InviteIcon />}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              if (!includes(blockedReasons, Reason.Inviting)) {
                sendInvite(program.id);
              }
            }}
          />
        );
      }
    }

    return {
      value: program.id,
      label: program.title,
      optionClass: cx(styles.programOption, styles.option, {
        [styles.noSelect]: isSelectingDisabled,
      }),      
      showActions: mode === 'default' && (isInvitingToThisProgram || !isNil(status)),
      actions,
    };
  };

  /**
   * Build all items for <Select>
   */
  const getSelectOptions = () => {
    const options: IOption[] = [];

    if (!isEmpty(statusMessage)) {
      options.push({
        value: null,
        label: <span>{statusMessage.message}</span>,
        optionClass: cx(statusMessage.className, styles.message, styles.option, styles.noSelect),
        onSelect: () => false,
      });
    }

    forEach(communitiesAndPrograms, (community) => {

      if (!isWorkflowEnabled) {
        options.push({
          value: null,
          label: community.title,
          optionClass: cx(styles.communityOption, styles.option, styles.noSelect),
          onSelect: () => false,
        });
      }

      if (isEmpty(community.programs)) {

        if (!isWorkflowEnabled) {
          options.push({
            value: null,
            label: 'No programs yet',
            optionClass: cx(styles.noPrograms, styles.programOption, styles.option, styles.noSelect),
          });
        }

      } else {

        options.push(
          ...map(community.programs, (program) => (
            getProgramOption(program)
          ))
        );
      }
    });
    return options;
  };

  /**
   * Build select options for all programs
   * This is used for isWorkflowEnabled === true
   */
  const getAllProgramsSelectOptions = () => {
    const options: IOption[] = [];

    if (!isEmpty(statusMessage)) {
      options.push({
        value: null,
        label: <span>{statusMessage.message}</span>,
        optionClass: cx(statusMessage.className, styles.message, styles.option, styles.noSelect),
        onSelect: () => false,
      });
    }

    if (isEmpty(allProgramsForProspect)) {
      options.push({
        value: null,
        label: 'No programs yet',
        optionClass: cx(styles.noPrograms, styles.programOption, styles.option, styles.noSelect),
      });
    }

    options.push(
      ...map(allProgramsForProspect, (program) => (
        getProgramOption(program)
      ))
    );

    return options;
  };

  /**
   * Render select element
   */
  const selectOptions = isWorkflowEnabled ? getAllProgramsSelectOptions() : getSelectOptions();
  const defaultProgramId = selectedProgramId || getRecentlyCreatedProgramId(programs);
  const defaultSelectedIndex = isNumber(defaultProgramId)
    ? findIndex(selectOptions, (option) => option.value === defaultProgramId)
    : null;

  // Update <Select>'s state if needed
  useEffect(() => {
    if (
      isNumber(defaultSelectedIndex) &&
      selectRef.current &&
      selectRef.current.state.selectedIndex !== defaultSelectedIndex
    ) {
      selectRef.current.selectOptionAtIndex(defaultSelectedIndex);
    }
  }, [defaultSelectedIndex]);

  const renderSelect = () => <>
    <Select
      {...selectProps} // Extend to other props below as needed
      ref={selectRef}
      className={cx(styles.select, selectProps.className)}
      buttonClassName={cx(styles.selectButton, {
        [styles.isFocused]: isPopoverShown,
      })}
      hintText='Invite to ...'
      options={selectOptions}
      defaultSelectedIndex={defaultSelectedIndex}
      onChange={(value) => {
        if (selectProps && isFunction(selectProps.onChange)) {
          selectProps.onChange();
        }
        onProgramSelect(value);
        updateSelectedProgramId(value);
        setIsPopoverShown(false);
      }}
      anchorLocation={selectProps.anchorLocation || 'button'}
      onMenuOpen={() => setIsPopoverShown(true)}
      onMenuClose={() => setIsPopoverShown(false)}
      popoverProps={{
        className: cx(styles.popover, {
          [styles.shown]: isPopoverShown,
        }),
        contentWrapperClassName: styles.popoverContentWrapper,
        contentClassName: styles.popoverContent,
        anchorOrigin: 'middle',
        showArrow: false,
        shadowSize: 'large',
        minWidth: styles.popoverWidth,
        maxWidth: styles.popoverWidth,
        mountRef: mainContainerRef,
        ...selectProps.popoverProps,
      }}
    />
  </>;


  const renderButton = () => (
    <div ref={submitButtonRef}>
      <SubmitButton
        className={cx(styles.inviteButton, {
          [styles.inviting]: includes(blockedReasons, Reason.InvitingThisProspect),
          [styles.wait]: includes(blockedReasons, Reason.Inviting),
          [styles.disabled]: isInviteBlocked,
        })}
        disabled={isInviteBlocked}
        isSubmitting={inviteStatus && inviteStatus.isInviting}
        submittingLabel=''
        label={programStatusOfProspect === 'invited' ? 'Invited' : 'Invite'}
        title={(() => {
          if (includes(blockedReasons, Reason.InvitingThisProspect)) {
            return 'Inviting...';
          }
        })()}
        onClick={() => !isInviteBlocked && sendInvite()}
      />
    </div>
  );

  const renderTooltip = () => {
    const firstBlockedReason = find(blockedReasons, (reason) => !isUndefined(ReasonsDescriptions[reason]));

    if (
      !isInviteBlocked
        || !firstBlockedReason
    ) {
      return null;
    }

    const descriptedReason = ReasonsDescriptions[firstBlockedReason];

    return (
      <Tooltip
        mountRef={submitButtonRef}
      >
        <div className={styles.tooltip}>
          {descriptedReason.message}
        </div>
      </Tooltip>
    );
  };

  return (
    <div
      className={cx(
        styles.Invite,
        styles.placeholderShimmy,
        className,
        {
          [styles.hideButton]: isPopoverShown,
          [styles.withButton]: mode === 'withButton',
          [styles.withBorder]: withBorder,
        },
      )}
      ref={mainContainerRef}
    >
      {renderTooltip()}
      <Loading
        className={styles.loading}
        show={isFetching}
        theme='blue'
        rounded
      />
      {renderSelect()}
      {renderButton()}
      {!toastRefProp ? <Toast ref={toastRef} /> : null}
    </div>
  );
};
